import { useState } from 'react';
import { Card, Button, Input, Textarea, Select, Label, Badge } from './../ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { humanize } from '../../lib/humanize';
import type { HonorAward } from '@scholarship-app/types';

interface Props {
  honors: HonorAward[];
  editing: boolean;
  onChange: (next: HonorAward[]) => void;
}

const empty = (): HonorAward => ({
  name: '',
  year: new Date().getFullYear(),
  level: 'school',
  description: '',
  featured: false,
});

export function ProfileHonorsEditor({ honors, editing, onChange }: Props) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<HonorAward | null>(null);

  function startAdd() {
    setAdding(true);
    setEditIndex(null);
    setDraft(empty());
  }
  function startEdit(i: number) {
    setAdding(false);
    setEditIndex(i);
    setDraft({ ...honors[i] });
  }
  function cancel() {
    setAdding(false);
    setEditIndex(null);
    setDraft(null);
  }
  function save() {
    if (!draft) return;
    if (adding) {
      onChange([...honors, draft]);
    } else if (editIndex !== null) {
      const next = [...honors];
      next[editIndex] = draft;
      onChange(next);
    }
    cancel();
  }
  function remove(i: number) {
    if (!confirm(`Delete honor: "${honors[i].name}"?`)) return;
    onChange(honors.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      {honors.length === 0 && !adding && (
        <p className="text-sm text-soft">No honors or awards yet.</p>
      )}
      {honors.map((h, i) => (
        <div key={i}>
          {editIndex === i && draft ? (
            <HonorEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
          ) : (
            <div className="flex items-start justify-between gap-2 bg-cream rounded-md px-3 py-2">
              <div className="text-sm flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong>{h.name}</strong>
                  {h.featured && <Badge variant="green">Featured</Badge>}
                  <Badge variant="gray">{humanize(h.level)}</Badge>
                  <span className="text-xs text-soft">{h.year}</span>
                </div>
                {h.description && <p className="text-xs text-soft mt-0.5">{h.description}</p>}
              </div>
              {editing && (
                <div className="flex gap-1 shrink-0">
                  <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(i)}><Pencil size={12} /></Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)}><Trash2 size={12} /></Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {adding && draft && (
        <HonorEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
      )}
      {editing && !adding && (
        <Button type="button" size="sm" variant="secondary" onClick={startAdd}>
          <Plus size={12} className="mr-1" />Add honor
        </Button>
      )}
    </div>
  );
}

function HonorEditForm({ draft, setDraft, onSave, onCancel }: { draft: HonorAward; setDraft: (d: HonorAward) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="md:col-span-2">
          <Label>Name</Label>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. HSF YLI Selectee" />
        </div>
        <div>
          <Label>Year</Label>
          <Input type="number" min="2000" max="2100" value={draft.year} onChange={(e) => setDraft({ ...draft, year: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Level</Label>
          <Select value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value as HonorAward['level'] })}>
            <option value="school">School</option>
            <option value="regional">Regional</option>
            <option value="state">State</option>
            <option value="national">National</option>
            <option value="institutional">Institutional</option>
          </Select>
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} />
          Featured (show on scholarship applications)
        </label>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
          <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
        </div>
      </div>
    </Card>
  );
}
